import { get, isUndefined, isNil as isNill, isArray, has } from "lodash";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { UseFormSetError } from "react-hook-form";

import Logger from "./logHandlers/Logger";
import { NppesStateList } from "../models/PhysicianDetails";
import { stateNameAbbreviations } from "../constants/StateAbbreviations";
import SnackbarUtils from "./SnackbarProvider";
import { DateFieldType } from "../constants/Date";
import { DropdownOptionKeys } from "../constants/FormComponents";
import { FormattedNumber } from "./PhoneNumber";
import { DocumentType } from "../constants/DocumentUpload";
import { CloudEnvironmentConfig } from "../constants/AppConfigs";
import { IS_LOCALHOST, REACT_APP_ENDPOINT_ENV } from "../constants/Config";
import { AppConfigs } from "../models/AppConfigs";
import { CheckEligibility } from "../models/CheckEligibility";
import { EligibilityStatus, EligibilityStatusNull } from "../constants/ReferralRequestor";

export const a11yProps = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
};

/**
 *
 * @param payload api payload
 * @returns paylaod in query string format
 */
export const GetQueryString = (payload: any) => {
  const QueryString = Object.keys(payload)
    .map((key) =>
      payload[key] !== null || undefined ? key + "=" + payload[key] : ""
    )
    .filter((str) => str !== "") // Filter out empty strings
    .join("&");

  return QueryString;
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - Param value is mandatory
export const length = (value: any) => Array.isArray(value) && value.length > 0;

//function to get the value at path of object
export const isNil = (value: any) => {
  if (value == undefined) {
    return true;
  }
  return false;
};

export const getValue = (
  inputObj: any,
  nestedProp: string,
  defaultValue?: any
) => {
  if (isUndefined(defaultValue)) {
    defaultValue = null;
  }
  return get(inputObj, nestedProp, defaultValue);
};

export const getHealthPlanValueType = (
  base64String: string | undefined,
  index: number
): string => {
  return !isUndefined(base64String) && !isNill(base64String)
    ? atob(base64String).split("|||")[index]
    : "";
};
/**
 *
 * @param array T[] array
 * @param from number from
 * @param to number to
 * @returns array
 */
export function arrayMove<T>(array: T[], from: number, to: number) {
  array = array.slice();
  array.splice(to < 0 ? array.length + to : to, 0, array.splice(from, 1)[0]);
  return array;
}

/**
 *
 *  @returns Array of objects of type State
 */

export const stateListData = (): NppesStateList[] => {
  return Object.entries(stateNameAbbreviations).map(([stateName, stateId]) => ({
    stateId,
    stateName,
  }));
};

/* *
 * @param state any
 * @param obj any
 *
 * @returns merged object of reducer state and object
 * this function used to merge state with object
 */

export const mergeObjects = (state: any, obj: any) => ({
  ...state,
  ...obj,
});

export const isEmptyValue = (obj: { [key: string]: any } | undefined | string): boolean => {
  if(obj !== null && obj !== "" && obj !== undefined) {
    return Object.values(obj).every(
      (value) => value === null || value === "" || value === undefined
    );
  }

  return true;
};

type KeyValueObject = { [key: string]: any };

export const getFormValues = (
  fields: string[],
  getValues: (field: string) => any
): KeyValueObject => {
  return fields.reduce((acc, field) => {
    acc[field] = getValues(field);
    return acc;
  }, {} as KeyValueObject);
};

/**
 *
 * @param list array
 * @param keyNames dynamic key name {label: "key1", value: "key2"}
 * @returns array of dropdown options []{label: "label", value: "value"}
 */
export const getList = (list: any[], keyNames: DropdownOptionKeys) => {
  if (isArray(list)) {
    return list.map((v: any) => extractDropdownOption(v, keyNames));
  }
  return [];
};

const extractDropdownOption = (item: any, keyNames: DropdownOptionKeys) => {
  const result: any = {};
  for (const [key, value] of Object.entries(keyNames)) {
    result[key] = item[value];
  }
  return result;
};

export const initAfterConfigLoad = (): void => {
  Logger.suppressConsole();
};

//below function used for sending error response to api call
export const ErrorResponse = (error: any) => {
  const msg = "Something went wrong!";
  switch (error.status) {
    case 422:
    case 400:
      return error;
    case 404:
    case 500:
      return error;
    default:
      SnackbarUtils.warning(msg);
      break;
  }
};

/**
 * @param {any}  restData -api request object
 * @param {string} DateFieldType - An param to change output format which don't have time on ui | DateFieldType enum"
 *
 * @returns {any} This is the final request object after formatted date
 */
export const userDate = (restData: any) => {
  if (Array.isArray(restData)) {
    for (const item of restData) {
      userDate(item);
    }
  } else if (typeof restData === "object") {
    for (const objectKey in restData) {
      if (has(DateFieldType, objectKey)) {
        if (restData[objectKey]) {
          restData[objectKey] = dayjs(restData[objectKey]).format("YYYY-MM-DD");
        }
      }
      userDate(restData[objectKey]);
    }
  }
  return restData;
};

export const GetEnvVariables = (): Partial<AppConfigs> => {
  let envConfigs: Partial<AppConfigs> = {};

  if (IS_LOCALHOST && process.env.NODE_ENV !== "production") {
     // eslint-disable-next-line @typescript-eslint/no-var-requires
    const localConfig = require("../configs/localConfig.json");
    const selectedEndpointEnv: string = REACT_APP_ENDPOINT_ENV;

    switch (selectedEndpointEnv) {
      case CloudEnvironmentConfig.SB1:
        envConfigs = localConfig?.[CloudEnvironmentConfig.SB1] ?? {};
        break;
      case CloudEnvironmentConfig.QA1:
        envConfigs = localConfig?.[CloudEnvironmentConfig.QA1] ?? {};
        break;
      case CloudEnvironmentConfig.DEV1:
        envConfigs = localConfig?.[CloudEnvironmentConfig.DEV1] ?? {};
        break;
      case CloudEnvironmentConfig.STG1:
        envConfigs = localConfig?.[CloudEnvironmentConfig.STG1] ?? {};
        break;
      case CloudEnvironmentConfig.SB2:
        envConfigs = localConfig?.[CloudEnvironmentConfig.SB2] ?? {};
        break;
      case CloudEnvironmentConfig.DEV2:
        envConfigs = localConfig?.[CloudEnvironmentConfig.DEV2] ?? {};
        break;
      case CloudEnvironmentConfig.QA2:
        envConfigs = localConfig?.[CloudEnvironmentConfig.QA2] ?? {};
        break;
    }
  }

  return envConfigs;
};

/**
 *
 * @param phoneNumber string formatted/not formatted
 * @returns formatted phone number
 */
export const getFormattedPhoneNo = (phoneNumber: string | null | undefined) => {
  if (isNill(phoneNumber) || isUndefined(phoneNumber)) {
    return phoneNumber;
  } else {
    return FormattedNumber(phoneNumber.replace(/\D/g, ""), phoneNumber.length);
  }
};

/* *
 * @param dob accepts Date of birth of patient
 * @param startDate  accepts start date of request
 * @param endDate    accepts end date of request
 *
 * @returns true if the patient is turning 21 in between
 * provided start and end date or is getting 21 before the start date
 */

export const is21OrTurning21Soon = (
  dob: string | Date,
  startDate: string,
  endDate: string
): boolean => {
  dayjs.extend(isSameOrBefore);
  dayjs.extend(isSameOrAfter);
  const twentyFirstBirthday = dayjs(dob).add(21, "year");
  const start = dayjs(startDate);
  const end = dayjs(endDate);

  return (
    (twentyFirstBirthday.isSameOrAfter(start) &&
      twentyFirstBirthday.isSameOrBefore(end)) ||
    twentyFirstBirthday.isSameOrBefore(start)
  );
};
/**
 *
 * @param value string value
 *
 * @returns extracted doc type
 */
export const extractDocType = (value: string | null | undefined) => {
  if (!value || !value.includes("/")) {
    return DocumentType.INVALID;
  }
  const splitString: any = value.split("/")[1];
  const format: DocumentType = splitString;
  return format;
};
export const getFormattedName = (str: string | null | undefined) => {
  if (!str) {
    return "";
  }

  return str
    .toLowerCase()
    .replace(/(^|\s|-)(\w)/g, (_, m1, m2) => m1 + m2.toUpperCase());
};

/**
 *
 * @param errorResponse any errorResponse
 * @param setError UseFormSetError<any> setError
 *
 * @returns server side error message
 */
export const serverSideErrorMsg = (
  errorResponse: any,
  setError: UseFormSetError<any>
) => {
  return Object.keys(errorResponse).forEach((field) => {
    const messages = errorResponse[field];
    if (messages && isArray(messages)) {
      setError(field as any, {
        type: "server",
        message: messages.join(". "),
      });
    }
  });
};

export const isEligible = (eligibilityData: CheckEligibility) => {
  const result = ((getValue(eligibilityData, "eligibilityStatus", "") ===
    EligibilityStatus.STATUS_TRUE || getValue(eligibilityData, "eligibilityStatus", "") !==
    EligibilityStatusNull )&& getValue(eligibilityData, "verificationServiceResponded", false) ===true) ? true : false
  return result
}